import { LANDING_COLORS, LANDING_KEYFRAMES } from '@/lib/landing-styles';

interface FloatingOrbsProps {
  variant?: 'default' | 'subtle' | 'hero';
  className?: string;
}

/**
 * FloatingOrbs - Decorative background elements with blur and float animation
 *
 * Usage:
 * <section className="relative overflow-hidden">
 *   <FloatingOrbs variant="hero" />
 *   <div className="relative z-10">Content here</div>
 * </section>
 *
 * Variants:
 * - default: Standard purple and green orbs
 * - subtle: Lower opacity for less prominent effect
 * - hero: Larger orbs with grid pattern overlay
 */
const FloatingOrbs = ({ variant = 'default', className = '' }: FloatingOrbsProps) => {
  const getOpacity = () => {
    switch (variant) {
      case 'subtle':
        return { primary: 0.15, secondary: 0.1 };
      case 'hero':
        return { primary: 0.3, secondary: 0.2 };
      default:
        return { primary: 0.25, secondary: 0.15 };
    }
  };

  const opacity = getOpacity();

  return (
    <>
      {/* Inject keyframes */}
      <style dangerouslySetInnerHTML={{ __html: LANDING_KEYFRAMES }} />

      <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}>
        {/* WhatsApp Green Orb - Top Right */}
        <div
          className="absolute w-96 h-96 rounded-full blur-3xl"
          style={{
            background: `radial-gradient(circle, ${LANDING_COLORS.whatsapp.green} 0%, transparent 70%)`,
            top: '-10%',
            right: '10%',
            opacity: opacity.primary,
            animation: 'float 8s ease-in-out infinite',
          }}
        />

        {/* Purple Orb - Bottom Left */}
        <div
          className="absolute w-80 h-80 rounded-full blur-3xl"
          style={{
            background: `radial-gradient(circle, ${LANDING_COLORS.primary} 0%, transparent 70%)`,
            bottom: '10%',
            left: '-5%',
            opacity: opacity.secondary,
            animation: 'float 10s ease-in-out infinite reverse',
          }}
        />

        {/* Additional orb for hero variant */}
        {variant === 'hero' && (
          <>
            {/* Accent Orb - Center */}
            <div
              className="absolute w-64 h-64 rounded-full blur-3xl"
              style={{
                background: `radial-gradient(circle, ${LANDING_COLORS.accent} 0%, transparent 70%)`,
                top: '40%',
                right: '30%',
                opacity: 0.1,
                animation: 'float 12s ease-in-out infinite',
              }}
            />

            {/* Grid pattern overlay */}
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `linear-gradient(${LANDING_COLORS.primary} 1px, transparent 1px), linear-gradient(90deg, ${LANDING_COLORS.primary} 1px, transparent 1px)`,
                backgroundSize: '60px 60px',
                opacity: 0.03,
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default FloatingOrbs;
